import React from 'react';
import moment from 'moment';
import {Box, Card, useTheme} from '@mui/material';
import {
  useGridApiRef,
  GridToolbarColumnsButton,
  GridColDef
} from '@mui/x-data-grid-premium';
import {ExpandLessOutlined, ExpandMoreOutlined} from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';

import {StripedDataGrid} from '@components/datagrid/DataGridComponentStyles';
import CustomPagination from '../table-custom-pagination/TableCustomPagination';
import DropDownPanel from 'src/sections/dropdown/drop-down-panel';
import ListSubheaderComponent from '../list-subheader/ListSubheader';

import {NoRowsOverlay, GridCellRender} from '../../grid-utils';
import EncryptUtils from 'src/utils/EncryptUtils';
import {APP} from 'src/styles/variables';
import {fCurrency} from 'src/utils/format-number';
import {rowStyles} from '@shared/utils/HelperUtils';
import {getGridSx} from '../../util';
import * as APIService from '@shared/services/service-handlers/PrivateService';
import {useBoolean} from 'src/hooks/use-boolean';

const BatchListing = (props: any) => {
  const theme = useTheme();
  const gridApiRef = useGridApiRef();
  const isMounted = React.useRef(false);
  const eraBatchListing = useBoolean();

  const [batchListingData, setBatchListingData] = React.useState<any>([]);
  const [batchListingGridLoading, setBatchListingGridLoading] =
    React.useState(false);

  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(25);
  const [pageCount, setPageCount] = React.useState(1);
  const [totalRecords, setTotalRecords] = React.useState(1);
  const [exportLoading, setExportLoading] = React.useState(false);

  const getBatchListing = async () => {
    try {
      const tk = EncryptUtils.encryptedData({
        transactionId: props.transactionId,
        batchId: props?.transactionInfo?.batchId,
        limit: pageSize,
        offset: (page - 1) * pageSize
      });
      setBatchListingGridLoading(true);
      const res = await APIService.getBatchListing({tk});
      setBatchListingData(res.data);
      setTotalRecords(res.totalRecords);
      setPageCount(res.pageCount);
      setBatchListingGridLoading(false);
    } catch (e) {
      setBatchListingGridLoading(false);
    }
  };

  const exportBatchListing = async () => {
    setExportLoading(true);
    try {
      const tk = EncryptUtils.encryptedData({
        transactionId: props.transactionId,
        batchId: props?.transactionInfo?.batchId
      });
      const response = await APIService.exportBatchListing({tk});
      setExportLoading(false);
      const eraFileName = props?.transactionInfo?.FileName;
      const filename =
        'ERA BATCH LISTING ' +
        eraFileName +
        ' ' +
        moment().format('YYYY-MM-DD hh:mm:ss') +
        '.xlsx';
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename); // Specify the filename
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      setExportLoading(false);
    }
  };

  const getStatusSx = (status: string) => {
    if (status === 'Posted') {
      return {
        // fontWeight: 'bold',
        color: 'success.main'
      };
    }
    return {
      //   fontWeight: 'bold',
      color: 'error.main'
    };
  };

  React.useEffect(() => {
    getBatchListing();
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  React.useEffect(() => {
    if (isMounted.current) {
      getBatchListing();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  // Render Pagination
  const Pagination = () => {
    return (
      <CustomPagination
        page={page}
        pageSize={pageSize}
        rowsPerPageOptions={[10, 25, 50, 100]}
        pageCount={pageCount}
        totalRecordsCount={totalRecords}
        totalRows={batchListingData.length}
        setPage={setPage}
        setPageSizes={setPageSize}
      />
    );
  };

  const CustomToolbar = () => {
    return (
      <Box sx={{display: 'flex', justifyContent: 'flex-start', padding: '4px'}}>
        <GridToolbarColumnsButton />
      </Box>
    );
  };

  const batchListingColumns: GridColDef[] = [
    {
      field: 's_no',
      headerName: 'S.No',
      type: 'number',
      sortable: false,
      filterable: false,
      flex: 1,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params: any) => (
        <GridCellRender
          value={params.api.getAllRowIds().indexOf(params.id) + 1}
        />
      )
    },
    {
      headerName: 'Check Number',
      field: 'checkOrEftTraceNumber',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 100,
      align: 'left',
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      headerName: 'Posted Status',
      field: 'PostingStatus',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 120,
      align: 'left',
      renderCell: (params: any) => (
        <GridCellRender
          typographySx={getStatusSx(params.value)}
          value={params.value}
        />
      )
    },
    {
      headerName: 'Reason',
      field: 'PostingStatusReason',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 120,
      align: 'left',
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      headerName: 'Patient Name',
      field: 'patientFullName',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 150,
      align: 'left',
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      headerName: 'Claim Number',
      field: 'claimNumber',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 120,
      align: 'left',
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      headerName: 'Encounter Number',
      field: 'encounterNumber',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 120,
      align: 'left',
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      headerName: 'Service Date',
      field: 'serviceDate',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 100,
      align: 'left',
      valueGetter: (params: any) =>
        params.value ? moment.utc(params.value).toDate() : null,
      renderCell: (params: any) => (
        <GridCellRender
          value={
            params.value ? moment.utc(params.value).format('MM/DD/YYYY') : null
          }
        />
      )
    },
    {
      headerName: 'Modifier1',
      field: 'modifier1',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 100,
      align: 'left'
    },
    {
      headerName: 'Modifier2',
      field: 'modifier2',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 100,
      align: 'left'
    },
    {
      headerName: 'Claim Status Code',
      field: 'claimStatusCode',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 100,
      align: 'left'
    },
    {
      headerName: 'Transaction Status',
      field: 'claimStatus',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 100,
      align: 'left'
    },
    {
      headerName: 'Billed',
      field: 'chargeAmount',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 100,
      align: 'left',
      renderCell: (params: any) => (
        <GridCellRender value={fCurrency(params.value)} />
      )
    },
    {
      headerName: 'Allowed',
      field: 'allowedAmount',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 100,
      align: 'left',
      renderCell: (params: any) => (
        <GridCellRender value={fCurrency(params.value)} />
      )
    },
    {
      field: 'adjustmentAmount',
      headerName: 'Adjustment',
      sortable: false,
      filterable: false,
      flex: 1,
      width: 100,
      renderCell: (params: any) => (
        <GridCellRender value={fCurrency(params.value)} />
      )
    },
    {
      headerName: 'Payment',
      field: 'paymentAmount',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 100,
      align: 'left',
      renderCell: (params: any) => (
        <GridCellRender value={fCurrency(params.value)} />
      )
    },
    {
      headerName: 'Discrepancy',
      field: 'discrepancyAmount',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 100,
      align: 'left',
      renderCell: (params: any) => (
        <GridCellRender value={fCurrency(params.value)} />
      )
    },
    {
      headerName: 'Disallow',
      field: 'disallowAmount',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 100,
      align: 'left',
      renderCell: (params: any) => (
        <GridCellRender value={fCurrency(params.value)} />
      )
    },
    {
      headerName: 'Charge-All',
      field: 'chargeAllAmount',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 100,
      align: 'left',
      renderCell: (params: any) => (
        <GridCellRender value={fCurrency(params.value)} />
      )
    },
    {
      headerName: 'Deductible',
      field: 'deductibleAmount',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 100,
      align: 'left',
      renderCell: (params: any) => (
        <GridCellRender value={fCurrency(params.value)} />
      )
    },
    {
      headerName: 'Co Insurance',
      field: 'coInsuranceAmount',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 100,
      align: 'left',
      renderCell: (params: any) => (
        <GridCellRender value={fCurrency(params.value)} />
      )
    },
    {
      headerName: 'Co Pay',
      field: 'coPayAmount',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 100,
      align: 'left',
      renderCell: (params: any) => (
        <GridCellRender value={fCurrency(params.value)} />
      )
    },
    {
      headerName: 'Patient Responsibility',
      field: 'patientResponsibilityAmount',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 100,
      align: 'left',
      renderCell: (params: any) => (
        <GridCellRender value={fCurrency(params.value)} />
      )
    },
    {
      headerName: 'Claim Level Paid',
      field: 'claimLevelPaymentAmount',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 100,
      align: 'left',
      renderCell: (params: any) => (
        <GridCellRender value={fCurrency(params.value)} />
      )
    },
    {
      headerName: 'Payer ID',
      field: 'payerIdentifier',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 100,

      align: 'left',
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      headerName: 'Payer Name',
      field: 'payerLastNameOrOrgName',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 170,
      align: 'left',
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      headerName: 'Additional Payee',
      field: 'additionalPayee',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 170,

      align: 'left',
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      headerName: 'Rendering Provider',
      field: 'renderingProviderFullName',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 170,

      align: 'left',
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      headerName: 'Policy Number',
      field: 'policyNumber',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 170,

      align: 'left',
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      headerName: 'Payee',
      field: 'payeeLastNameOrOrgName',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 120,

      align: 'left',
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      headerName: 'NPI',
      field: 'npi',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 120,

      align: 'left',
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      field: 'lateFilingAmount',
      headerName: 'Late Filing Amount',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 120,

      align: 'left',
      renderCell: (params: any) => (
        <GridCellRender value={fCurrency(params.value)} />
      )
    },
    {
      field: 'adjudicationCodes',
      headerName: 'Adjudication Codes',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 120,

      align: 'left',
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      field: 'perInformation',
      headerName: 'PER Information',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 120,

      align: 'left',
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      field: 'cagcCode1',
      headerName: 'CAGC/RARC1',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 80,

      align: 'left',
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      field: 'cagcDescription1',
      headerName: 'CAGC/RARC1 Description',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 150,

      align: 'left',
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      field: 'cagcCode2',
      headerName: 'CAGC/RARC1',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 80,

      align: 'left',
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      field: 'cagcDescription2',
      headerName: 'CAGC/RARC2 Description',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 150,

      align: 'left',
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      field: 'cagcCode3',
      headerName: 'CAGC/RARC1',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 80,

      align: 'left',
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      field: 'cagcDescription3',
      headerName: 'CAGC/RARC3 Description',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 150,

      align: 'left',
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      field: 'cagcCode4',
      headerName: 'CAGC/RARC4',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 80,

      align: 'left',
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      field: 'cagcDescription4',
      headerName: 'CAGC/RARC4 Description',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 150,

      align: 'left',
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      field: 'rarcCode',
      headerName: 'RARC',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 80,

      align: 'left',
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      field: 'rarcDesc',
      headerName: 'RARC Description',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 150,

      align: 'left',
      renderCell: (params: any) => <GridCellRender value={params.value} />
    }
  ];

  const renderDataGridComponent = () => {
    const gridLoading = batchListingGridLoading;
    const gridColumns = batchListingColumns;
    const gridRows = batchListingData;
    const pagination = true;
    const horizontalScroll = true;
    const hideFooter = false;
    return (
      <StripedDataGrid
        showColumnVerticalBorder
        showCellVerticalBorder
        autoHeight
        disableRowGrouping
        disableRowSelectionOnClick
        disableColumnSelector
        disableAggregation
        checkboxSelection={false}
        apiRef={gridApiRef}
        loading={gridLoading}
        columns={gridColumns}
        rows={gridRows}
        rowThreshold={0}
        rowHeight={37}
        columnHeaderHeight={42}
        hideFooter={hideFooter}
        pagination={pagination}
        paginationMode="server"
        sortingMode="server"
        sortingOrder={['desc', 'asc']}
        filterMode="server"
        getRowId={(row: any) => row.id || row.ID}
        slotProps={{
          filterPanel: {
            sx: {'& .MuiDataGrid-filterFormValueInput': {width: 'auto'}}
          }
        }}
        getRowClassName={(params: any) => {
          return rowStyles(params);
        }}
        localeText={{
          toolbarColumns: 'Manage Columns'
        }}
        sx={getGridSx({
          theme,
          APP,
          gridHeight: 'auto',
          checkboxColor: props?.checkboxColor,
          horizontalScroll: horizontalScroll,
          minHeight: '100px'
        })}
        slots={{
          detailPanelExpandIcon: ExpandMoreOutlined,
          detailPanelCollapseIcon: ExpandLessOutlined,
          pagination: Pagination,
          toolbar: CustomToolbar,
          noRowsOverlay: () => (
            <NoRowsOverlay
              noDataFound
              theme={theme}
              loading={batchListingGridLoading}
            />
          ),
          noResultsOverlay: () => (
            <NoRowsOverlay
              noRowsFound
              theme={theme}
              loading={batchListingGridLoading}
            />
          )
        }}
      />
    );
  };

  return (
    <Box sx={{width: '100%'}}>
      <ListSubheaderComponent
        onClose={props?.onClose}
        label={props?.paymentPageLabel}
      />
      <Box px={3} mt={1}>
        <Card sx={{p: 3, boxShadow: 5, borderRadius: '8px', mb: 1}}>
          <DropDownPanel
            title={`Batch Listing - ${props?.transactionInfo?.BatchName}`}
            collapse={eraBatchListing.value}
            onCollapse={eraBatchListing.onToggle}
            secondaryTitle={
              <Box sx={{display: 'flex', alignItems: 'center'}}>
                <LoadingButton
                  type="submit"
                  variant="outlined"
                  onClick={getBatchListing}
                  sx={{
                    backgroundColor: theme.palette.primary.light
                  }}
                  loadingPosition="start">
                  Refresh
                </LoadingButton>
                <LoadingButton
                  type="submit"
                  variant="contained"
                  loading={exportLoading}
                  onClick={exportBatchListing}
                  sx={{
                    marginLeft: '12px',
                    backgroundColor: theme.palette.primary.dark
                  }}
                  loadingPosition="start">
                  Export
                </LoadingButton>
              </Box>
            }>
            {renderDataGridComponent()}
          </DropDownPanel>
        </Card>
      </Box>
    </Box>
  );
};

export default BatchListing;
